// GuardedApp — the reusable guarded app-mount used by primary and split panes.
import { Suspense } from "react";
import { Wrench } from "lucide-react";
import {
  appCanMountAt,
  canonicalSubAppId,
  getSubApp,
  ZARVIS_APP_ID,
  type ScopeLevel,
} from "@perlab/registry";
import { useAuth } from "@perlab/auth-bridge";
import { PD25_SCOUT_MOUNT_ENABLED, ScoutSubAppView } from "../scout-iframe-mount";
import { INLINE_SUBAPPS } from "../sub-apps/inline-registry";
import { PageContainer } from "./PageContainer";
import { EmptyState } from "./EmptyState";
import { ShellPending } from "./ShellPending";
import { SubAppBoundary } from "./SubAppBoundary";
import { useMaintenanceMode } from "../state/useMaintenanceMode";
import { useEntitlements } from "../../lib/auth/EntitlementsContext";

const INTERNAL_ONLY_TIER = "kearney-internal-only";

export interface GuardedAppProps {
  appId: string | undefined;
  currentLevel: ScopeLevel;
  levelPending: boolean;
  levelError?: "unavailable" | "missing" | null;
}

export function GuardedApp({ appId, currentLevel, levelPending, levelError }: GuardedAppProps): React.ReactElement {
  const canonicalAppId = appId ? canonicalSubAppId(appId) : undefined;
  const manifest = canonicalAppId ? getSubApp(canonicalAppId) : undefined;
  const InlineApp = canonicalAppId ? INLINE_SUBAPPS[canonicalAppId] : undefined;
  const isScoutRoute = canonicalAppId === "scout";
  const { hasAppAccess } = useEntitlements();

  const mountLegal = manifest ? appCanMountAt(manifest, currentLevel) : false;
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const hasInternalRole = roles.some((role) => role !== "ClientUser");
  const tier = manifest?.permissions?.clientAccessTier ?? INTERNAL_ONLY_TIER;
  const clientTierDenies = Boolean(manifest) && tier === INTERNAL_ONLY_TIER && !hasInternalRole;
  const appEntitlementDenies = Boolean(manifest && canonicalAppId && !hasAppAccess(canonicalAppId));
  const maintenance = useMaintenanceMode();

  if (maintenance) {
    return (
      <PageContainer data-app="subapp-maintenance-lockdown">
        <EmptyState
          icon={<Wrench size={28} aria-hidden />}
          title="Apps are paused for maintenance"
          description="The workspace is in maintenance mode, so apps are temporarily unavailable. Your data is safe — try again once maintenance ends."
        />
      </PageContainer>
    );
  }

  if (appEntitlementDenies) {
    return (
      <PageContainer data-app="subapp-entitlement-denied">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">{manifest?.name}</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          This app is not enabled for your account. Ask a SuperAdmin to grant it from User management.
        </p>
      </PageContainer>
    );
  }

  if (levelError) {
    return (
      <PageContainer data-app="subapp-scope-unavailable">
        <div role="alert">
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
            {levelError === "unavailable" ? "Workspace scope could not be loaded" : "Workspace scope is unavailable"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-text-secondary)]">
            {levelError === "unavailable"
              ? "Check your connection and refresh the page to try again."
              : "Open an available scope from the workspace navigation. If you expected access here, contact your workspace administrator."}
          </p>
        </div>
      </PageContainer>
    );
  }

  if (manifest && !mountLegal && !levelPending) {
    return (
      <PageContainer data-app="subapp-mount-denied">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">{manifest.name}</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          {manifest.name} doesn&apos;t mount at the {currentLevel} level (it mounts at:{" "}
          {(manifest.mountLevels ?? ["project"]).join(", ")}). Open it from the App Launcher at a
          matching scope.
        </p>
      </PageContainer>
    );
  }

  if (manifest && clientTierDenies && !levelPending) {
    return (
      <PageContainer data-app="subapp-client-tier-denied">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">{manifest.name}</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          {manifest.name} is an internal-only app and isn&apos;t available on your account.
        </p>
      </PageContainer>
    );
  }

  if (levelPending) {
    return (
      <PageContainer data-app="subapp-level-pending">
        <ShellPending />
      </PageContainer>
    );
  }

  if (manifest?.mode === "inline" && InlineApp) {
    const fillAvailableHeight = canonicalAppId === ZARVIS_APP_ID;
    return (
      <SubAppBoundary
        app={canonicalAppId ?? "unknown"}
        fillAvailableHeight={fillAvailableHeight}
      >
        <div
          className={fillAvailableHeight
            ? "subapp-inline-host h-full min-h-0 overflow-hidden"
            : "subapp-inline-host"}
          data-app={canonicalAppId}
        >
          <Suspense fallback={<ShellPending />}>
            <InlineApp />
          </Suspense>
        </div>
      </SubAppBoundary>
    );
  }

  return (
    <SubAppBoundary app={canonicalAppId ?? "unknown"}>
      <div className="subapp-placeholder" data-pd25-scout-mount-enabled={String(PD25_SCOUT_MOUNT_ENABLED)}>
        <h1>App: {canonicalAppId ?? "—"}</h1>
        <p>Sub-app mount point — wired to the native/iframe lane in Phase 1/2.</p>
        {isScoutRoute && PD25_SCOUT_MOUNT_ENABLED && <ScoutSubAppView />}
      </div>
    </SubAppBoundary>
  );
}
